import React from "react"
import { useDispatch } from "react-redux"
import { LoginForm } from "./Login/Login"
import { SignupForm } from "./Signup/Signup"
import { Loader, Panel } from "dash-styled"
import { AuthFormView } from "../../../Models/Client/AuthFormView"
import { ForgotPasswordForm } from "./ForgotPassword/ForgotPassword"
import { ResetPasswordForm } from "./ResetPassword/ResetPassword"
import { VerifyOTPForm } from "./VerifyOTP/VerifyOTP"
import { isAuthTransitioning } from "../../../Helpers/Functions/Selectors/auth"
import { LinkButton } from "../LinkButton/LinkButton"
import { push } from "connected-react-router"
import { routePaths } from "../../../Routes"
import { useAppState } from "../../../Hooks/useReduxState"
import { SignupConfirmForm } from "./SignupConfirm/SignupConfirm"
import { useLocation } from "react-router-dom"
import AzureOAuth from "./AzureOAuth/AzureOAuth"

interface AuthFormProps {
    view: AuthFormView
    squashSuccessMessage: boolean
    prefilledEmail?: string
    onLoginSuccess?: () => void
    authProvider?: string
}

export const AuthForm = ({
    view,
    onLoginSuccess,
    squashSuccessMessage,
    prefilledEmail,
    authProvider
}: AuthFormProps) => {
    const isProcessing = useAppState(isAuthTransitioning)
    const dispatch = useDispatch()
    const location = useLocation()
    const handleOnForgotSubmit = () =>
        dispatch(push(routePaths.authResetPassword()))
    const handleOnResetSubmit = () => dispatch(push(routePaths.authSignIn()))
    const forgotPassViewChange = () =>
        dispatch(push(routePaths.authForgotPassword()))
    const resetPassViewChange = () =>
        dispatch(push(routePaths.authResetPassword()))
    const verifyOTPViewChange = () =>
        dispatch(
            push({
                pathname: routePaths.authVerify(),
                search: location.search
            })
        )
    const renderForm = () => {
        switch (view) {
            case AuthFormView.login:
                return (
                    <LoginForm
                        onSuccess={verifyOTPViewChange}
                        onForgetPassword={forgotPassViewChange}
                        squashSuccessMessage={squashSuccessMessage}
                        prefilledEmail={prefilledEmail}
                    />
                )
            case AuthFormView.signup:
                return (
                    <SignupForm
                        prefilledEmail={prefilledEmail}
                        squashSuccessMessage={squashSuccessMessage}
                        onLoginSuccess={onLoginSuccess}
                    />
                )
            case AuthFormView.verify_otp:
                return (
                    <VerifyOTPForm
                        onSuccess={onLoginSuccess}
                        squashSuccessMessage={squashSuccessMessage}
                    />
                )
            case AuthFormView.signup_confirm:
                if (prefilledEmail === undefined) {
                    return (
                        <SignupForm
                            squashSuccessMessage={squashSuccessMessage}
                            onLoginSuccess={onLoginSuccess}
                        />
                    )
                }
                return (
                    <SignupConfirmForm
                        onSuccess={onLoginSuccess}
                        squashSuccessMessage={squashSuccessMessage}
                        email={prefilledEmail}
                    />
                )
            case AuthFormView.forgot:
                return (
                    <>
                        <ForgotPasswordForm onSubmit={handleOnForgotSubmit} />
                        <LinkButton
                            onClick={resetPassViewChange}
                            className="mt-4 flex items-center mx-auto"
                        >
                            I already have a code
                        </LinkButton>
                    </>
                )
            case AuthFormView.reset:
                return (
                    <>
                        <ResetPasswordForm onSubmit={handleOnResetSubmit} />
                        <LinkButton
                            testId="request-code"
                            onClick={forgotPassViewChange}
                            className="mt-4 flex items-center mx-auto"
                        >
                            I need to request a code
                        </LinkButton>
                    </>
                )
            case AuthFormView.login_provider:
                return <AzureOAuth provider={authProvider} />
        }
    }

    return (
        <Panel className="relative w-full" size="lg">
            {renderForm()}
            {isProcessing ? <Loader absolute={true} /> : null}
        </Panel>
    )
}
